import type { Reference, Resource } from '@medplum/fhirtypes';

import { classifyReference } from './reference';

/**
 * Flat lookup of every resource available to render, keyed 'Type/id'. Built
 * from whatever the page fetched (a search bundle with _include, a plain
 * list). References that miss the map degrade to stubs — a resource the
 * server withheld (403) is indistinguishable from one that does not exist.
 */
export type ResolutionMap = ReadonlyMap<string, Resource>;

export function resourceKey(resource: Resource): string | undefined {
  return resource.id ? `${resource.resourceType}/${resource.id}` : undefined;
}

export function buildResolutionMap(
  resources: readonly Resource[],
): ResolutionMap {
  const map = new Map<string, Resource>();
  for (const resource of resources) {
    const key = resourceKey(resource);
    if (key) map.set(key, resource);
  }
  return map;
}

/** Relative-reference join. Contained/absolute/logical targets are not resolvable here (yet) and degrade to stubs. */
export function lookupReference(
  map: ResolutionMap,
  ref: Reference,
): Resource | undefined {
  const classified = classifyReference(ref);
  if (classified.kind !== 'relative') return undefined;
  return map.get(`${classified.resourceType}/${classified.id}`);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Flattens a FHIR GraphQL response into standalone resources. FHIR GraphQL
 * resolves reference targets inline as `{ reference, display, resource }`;
 * the walker instead joins references through the resolution map — so nested
 * `resource` payloads are collected as their own resources and stripped from
 * the reference wrapper. Any object carrying `resourceType` + `id` anywhere
 * in the payload is collected. `__typename` noise is left in place — the
 * renderer ignores it.
 */
export function flattenFhirGraphQLData(data: unknown): Resource[] {
  const collected = new Map<string, Resource>();

  function walk(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(walk);
    if (!isRecord(value)) return value;

    const processed: Record<string, unknown> = {};
    for (const [key, child] of Object.entries(value)) {
      // Nested reference target: collect it, strip it from the wrapper.
      if (key === 'resource' && isRecord(child) && 'resourceType' in child) {
        walk(child);
        continue;
      }
      processed[key] = walk(child);
    }

    if (
      typeof processed['resourceType'] === 'string' &&
      typeof processed['id'] === 'string'
    ) {
      const resource = processed as unknown as Resource;
      collected.set(`${resource.resourceType}/${resource.id}`, resource);
    }
    return processed;
  }

  walk(data);
  return [...collected.values()];
}

/**
 * Keys ('Type/id') referenced anywhere inside the given resources. Used to
 * pick render roots: a resource no other fetched resource points at.
 */
export function collectReferencedKeys(
  resources: readonly Resource[],
): ReadonlySet<string> {
  const referenced = new Set<string>();

  function walk(value: unknown): void {
    if (Array.isArray(value)) {
      for (const item of value) walk(item);
      return;
    }
    if (!isRecord(value)) return;
    const ref = value['reference'];
    if (typeof ref === 'string') {
      const classified = classifyReference({ reference: ref });
      if (classified.kind === 'relative') {
        referenced.add(`${classified.resourceType}/${classified.id}`);
      }
    }
    for (const child of Object.values(value)) walk(child);
  }

  for (const resource of resources) walk(resource);
  return referenced;
}
